import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { AdminLayout } from '@/app/layouts/AdminLayout';
import { RootLayout } from '@/app/layouts/RootLayout';
import { AdminConflictsPage } from '@/features/admin/AdminConflictsPage';
import { AdminControlPage } from '@/features/admin/AdminControlPage';
import { AdminMatchesPage } from '@/features/admin/AdminMatchesPage';
import { AdminSettingsPage } from '@/features/admin/AdminSettingsPage';
import { AdminTeamsPage } from '@/features/admin/AdminTeamsPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { MatchDetailPage } from '@/features/matches/MatchDetailPage';
import { PresentationPage } from '@/features/presentation/PresentationPage';
import { SchedulePage } from '@/features/schedule/SchedulePage';
import { GroupsPage } from '@/features/standings/GroupsPage';
import { KnockoutPage } from '@/features/standings/KnockoutPage';
import { TeamProfilePage } from '@/features/team/TeamProfilePage';
import { TeamRegistrationPage } from '@/features/team/TeamRegistrationPage';

import { NotFoundPage } from './NotFoundPage';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { routePaths } from './routePaths';
import { RequireAdmin } from './guards/RequireAdmin';
import { RequireAuth } from './guards/RequireAuth';
import { HomeRoute } from './HomeRoute';

/**
 * Route table. Feature areas are mounted here as they are implemented; the
 * router itself stays free of business rules.
 *
 * The guards are convenience only. What actually protects the data are the
 * Firestore security rules — see docs/SECURITY.md.
 */
const router = createBrowserRouter([
  {
    // Public, full-screen beamer view — no shared chrome, no login.
    path: routePaths.presentation,
    element: <PresentationPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: routePaths.home, element: <HomeRoute /> },
      { path: routePaths.login, element: <LoginPage /> },

      {
        element: <RequireAuth />,
        children: [
          { path: routePaths.team.profile, element: <TeamProfilePage /> },
          { path: routePaths.team.registration, element: <TeamRegistrationPage /> },
          { path: routePaths.tournament.schedule, element: <SchedulePage /> },
          { path: routePaths.tournament.groups, element: <GroupsPage /> },
          { path: routePaths.tournament.knockout, element: <KnockoutPage /> },
          { path: routePaths.tournament.matchPattern, element: <MatchDetailPage /> },
        ],
      },

      {
        element: <RequireAdmin />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { path: routePaths.admin.root, element: <AdminControlPage /> },
              { path: routePaths.admin.teams, element: <AdminTeamsPage /> },
              { path: routePaths.admin.matches, element: <AdminMatchesPage /> },
              { path: routePaths.admin.conflicts, element: <AdminConflictsPage /> },
              { path: routePaths.admin.settings, element: <AdminSettingsPage /> },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
